import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Inject,
  Injector,
  OnInit,
  signal,
  TemplateRef
} from '@angular/core';
import { ErrorPopupComponent } from '@pc/error-popup';
import { BreakpointObserverService } from '@pc/shared';
import { PcAlertsImages, PcButtonIcon, PcIcon, PcImages } from '@pc/common';
import { PcButtonType } from '@pc/gradient-btn';
import { IconComponent } from '@pc/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { ResponsiveAbstractCardComponent } from '@pc/responsive-abstract-card';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AbstractDialogData, AnimationDialogService } from '@pc/dialog-abstract';
import { SummaryCollapsibleCardComponent } from '../../../shared/components/summary-collapsible-card/summary-collapsible-card.component';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { LeadInsertService } from '../../../shared/services/lead-insert.service';
import { LeadInsertRequest } from '../../../shared/models/lead-insert.interface';
import { MainStore } from '../../../store/main-store/main-store';
import { ProcessTypeStore } from '../../../store/process-type-store/process-type-store';
import { TravelersStore } from '../../../core/stores/travelers.store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ReferralPopupData {
  title: string;
  titleAfterConfirmation: string;
  textInformation: string;
  textInformationAfterConfirmation: string;
  textSubInformation: string;
  textSubInformationAfterConfirmation: string;
  buttonText: string;
  buttonTextAfterConfirmation: string;
  secondaryButtonText: string;
  collapsibleCardTitle: string;
  isCloseIcon: boolean;
  summaryTemplate: TemplateRef<unknown>;
  notes?: string;
  desktopImg?: string;
  mobileImg?: string;
}

@Component({
  selector: 'pc-referral-popup',
  standalone: true,
  imports: [
    ErrorPopupComponent,
    IconComponent,
    TranslateModule,
    ResponsiveAbstractCardComponent,
    NgTemplateOutlet,
    SummaryCollapsibleCardComponent
  ],
  templateUrl: './referral-popup.component.html',
  styleUrl: './referral-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferralPopupComponent implements OnInit {
  public desktopImgPath = signal<string>('');
  public mobileImgPath = signal<string>('');
  public textInformation = signal<string>('');
  public textSubInformation = signal<string>('');
  public title = signal<string>('');
  public buttonText = signal<string>('');
  public secondaryButtonText = computed(() => this.data.event?.secondaryButtonText || '');
  public isCloseIcon = computed(() => this.data.event?.isCloseIcon as boolean);
  public notes = computed(() => this.data.event?.notes || this.textInformation());
  public smallPopup = signal<boolean>(true);
  public summaryTemplate = computed(() => this.data.event?.summaryTemplate as TemplateRef<unknown>);
  public isSummaryExpanded = signal<boolean>(true);
  public summaryCardTitle = computed(() => this.data.event?.collapsibleCardTitle || '');
  public injector = inject(Injector);
  public breakpoint = inject(BreakpointObserverService);
  public isDesktop = this.breakpoint.getMediumBreakpoint(this.injector);

  protected readonly PcButtonIcon = PcButtonIcon;
  protected readonly pcImages = PcImages;
  protected readonly PcButtonType = PcButtonType;
  protected readonly PcIcon = PcIcon;
  protected readonly PcAlertsImages = PcAlertsImages;

  private leadInsertService = inject(LeadInsertService);
  private animationDialogService = inject(AnimationDialogService);
  private dialogRef = inject(DialogRef);
  private destroyRef = inject(DestroyRef);
  private analytics = inject(AnalyticsService);
  private agentStore = inject(ProcessTypeStore);
  private mainStore = inject(MainStore);
  private travelerStore = inject(TravelersStore);

  constructor(@Inject(DIALOG_DATA) public data: AbstractDialogData<ReferralPopupData>) {}

  ngOnInit(): void {
    this.dataHandler();

    this.leadInsertService.insertLead(this.getLeadData()).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.analytics.pushGeneralEvent('generate_lead', { type: 'Other' });
  }

  public closeButton(): void {
    this.animationDialogService.closeDialogWithAnimation(this.dialogRef);
  }

  public closeWithSubmit(): void {
    if (this.smallPopup()) {
      this.smallPopup.set(false);
      this.buttonText.set(this.data.event?.buttonTextAfterConfirmation || '');
      this.textInformation.set(this.data.event?.textInformationAfterConfirmation || '');
      this.textSubInformation.set(this.data.event?.textSubInformationAfterConfirmation || '');
      this.title.set(this.data.event?.titleAfterConfirmation || '');
    } else {
      this.closeButton();
    }
  }

  public onExpandedClick(): void {
    this.isSummaryExpanded.set(!this.isSummaryExpanded());
  }

  private getLeadData(): LeadInsertRequest {
    return {
      firstName: this.travelerStore.adultTravelers()[0].firstName,
      lastName: this.travelerStore.adultTravelers()[0].surName,
      phoneCountryId: String(this.travelerStore.adultTravelers()[0].mobileCountryId),
      phonePhone: String(this.travelerStore.adultTravelers()[0].phoneNumber?.replace(/\s+/g, '')),
      email: this.travelerStore.adultTravelers()[0].email || '',
      notes: this.notes(),
      referredBy: this.mainStore.getReferredById(),
      agentSaleNumberID: this.agentStore.agentSaleNumberDetails()?.id,
      associationId: this.agentStore.organizationId(),
      selectedStateId: this.travelerStore.stateId(),
      siteUserID: this.agentStore.siteUserId() || '',
      leadId: this.agentStore.leadId() || ''
    };
  }

  private dataHandler(): void {
    this.title.set(this.data.event?.title || '');
    this.textInformation.set(this.data.event?.textInformation || '');
    this.textSubInformation.set(this.data.event?.textSubInformation || '');
    this.buttonText.set(this.data.event?.buttonText || '');
    this.desktopImgPath.set(this.data.event?.desktopImg || PcAlertsImages.Desktop_ReferToSales);
    this.mobileImgPath.set(this.data.event?.mobileImg || PcAlertsImages.ReferToSales);
  }
}
